import { makeStyles } from '@material-ui/core';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useCallback, useState } from 'react';
import {
  useResourceActionsDialogContext,
  useUpdateResourceAllocationForRequest
} from '~/modules/projects/resourcing-plan/hooks';
import {
  ResourceAllocationPeriodEditPopoverDetails,
  ResourceRequestUserAllocationBlockOverlayPeriod
} from '~/modules/resourcing/common/components';
import { useProjectContext } from '~/modules/resourcing/common/contexts';
import {
  useAvailabilityContext,
  withAvailabilityContextProvider
} from '~/modules/resourcing/common/hooks/useAvailabilityContext';
import usePeriodEditorChangeHandlers from '~/modules/resourcing/common/hooks/usePeriodEditorChangeHandlers';
import { useDeleteResourceAllocation } from '~/modules/resourcing/hooks/useDeleteResourceAllocation';

const useStyles = makeStyles(theme => ({
  root: {
    marginTop: theme.spacing(1)
  },
  timeContainer: {
    flexGrow: 0,
    flexShrink: 0,
    position: 'relative',
    overflow: 'hidden',
    transform: `translateY(-${theme.spacing(0.5)}px)`
  }
}));

const useDynamicItemContainerStyles = makeStyles({
  dynamicContainer: {
    minHeight: ({ dynamicContainerHeight, totalAllocations }) =>
      Math.max(dynamicContainerHeight / Math.max(1, totalAllocations))
  }
});

export const ResourceAllocationUsers = ({
  scale,
  isEditable = false,
  resourceRequest,
  resourceAllocations,
  chartDates,
  chartDisplayDateRange,
  dynamicContainerHeight
}) => {
  const classes = useStyles();
  const dynamicClasses = useDynamicItemContainerStyles({
    dynamicContainerHeight,
    totalAllocations: resourceAllocations.length
  });

  const {
    setResourceActionContextDetails,
    openRecallResourcesDialog,
    openReleaseResourcesDialog
  } = useResourceActionsDialogContext();

  const [allocationPeriodEditTarget, setAllocationPeriodEditTarget] = useState(
    null
  );

  const { onPatchResourceAllocation } = useUpdateResourceAllocationForRequest();
  const { project } = useProjectContext();
  const deleteResourceAllocationFromProject = useDeleteResourceAllocation({
    projectUri: project.id,
    resourceRequestId: resourceRequest.id
  });

  const projectDefaultScheduleRule = project.defaultScheduleRule;

  const { setAvailabilityLoading } = useAvailabilityContext();

  const onAllocationPeriodClick = ({ allocation }) => {
    setResourceActionContextDetails({
      resourceRequestId: resourceRequest.id,
      resourceAllocation: allocation
    });
  };

  const onPeriodClose = useCallback(
    (evt, originalAllocation) => {
      setAvailabilityLoading();

      onPatchResourceAllocation({
        project,
        openReleaseResourcesDialog,
        openRecallResourcesDialog,
        deleteResourceAllocationFromProject,
        allocation: {
          ...evt.values,
          requestedRoleUri: resourceRequest.role?.id
        },
        originalAllocation
      });
    },
    [
      setAvailabilityLoading,
      onPatchResourceAllocation,
      project,
      openReleaseResourcesDialog,
      openRecallResourcesDialog,
      deleteResourceAllocationFromProject,
      resourceRequest.role?.id
    ]
  );

  const {
    handleAllocationPeriodClick,
    handleAllocationPeriodClose,
    setPreviousPeriod,
    setNextPeriod
  } = usePeriodEditorChangeHandlers({
    allocationPeriodEditTarget,
    onPeriodClick: onAllocationPeriodClick,
    chartDates,
    setAllocationPeriodEditTarget
  });

  return (
    <div className={classes.root}>
      {resourceAllocations.map(resourceAllocation => (
        <div
          key={resourceAllocation.id}
          className={classNames(dynamicClasses, {
            [classes.timeContainer]: resourceAllocations.length > 1
          })}
        >
          <ResourceRequestUserAllocationBlockOverlayPeriod
            scale={scale}
            isEditable={isEditable}
            showRequestBlock={false}
            chartDisplayDateRange={chartDisplayDateRange}
            resourceRequest={resourceRequest}
            projectDefaultScheduleRule={projectDefaultScheduleRule}
            allocation={resourceAllocation}
            handleAllocationPeriodClick={handleAllocationPeriodClick}
            resourceAvailabilitySummarySeries={[]}
            onAllocationChange={onPeriodClose}
            showResizeIndicator
          />
        </div>
      ))}
      {isEditable && allocationPeriodEditTarget?.anchorEl && (
        <ResourceAllocationPeriodEditPopoverDetails
          resourceRequest={resourceRequest}
          chartStartDate={chartDates[0].start}
          chartEndDate={chartDates[chartDates.length - 1].end}
          scale={scale}
          allocationPeriodEditDetails={allocationPeriodEditTarget}
          onAllocationChange={onPeriodClose}
          isAllocationComplete
          handleAllocationPeriodClose={handleAllocationPeriodClose}
          setNextPeriod={setNextPeriod}
          setPreviousPeriod={setPreviousPeriod}
        />
      )}
    </div>
  );
};

ResourceAllocationUsers.propTypes = {
  scale: PropTypes.string.isRequired,
  resourceRequest: PropTypes.object.isRequired,
  isEditable: PropTypes.bool,
  chartDates: PropTypes.array.isRequired,
  resourceAllocations: PropTypes.array.isRequired,
  chartDisplayDateRange: PropTypes.object.isRequired,
  dynamicContainerHeight: PropTypes.number,
  isPsaRmpUserSchedulePerfFixEnabled: PropTypes.bool
};

export default withAvailabilityContextProvider(ResourceAllocationUsers);
